import dayjs from 'dayjs';
import { CoachOnboardingBriefing } from '../../types/opsBriefing';
import { QUIET_DAYS } from './coachOnboarding';

/**
 * 카드 처리 이력 — **체크(조용한 모드)와 오늘만 가리기** (정책 §4-3).
 *
 * ─────────────────────────────────────────────────────────────
 * 두 행동은 뜻이 다르다
 * ─────────────────────────────────────────────────────────────
 * · **체크 =「오늘 연락했다」** → 3일간 조용한 모드. 카드는 남지만 독촉을
 *   멈춘다. 연락했는데 매일 같은 말을 들으면, 원장님은 **자기 잘못이 아닌
 *   일로** 시달린다. 3일 뒤에도 안 풀렸으면 「연락 후에도 수락이 없습니다」로
 *   돌아온다 — 그건 새 사실이라 다시 말할 값어치가 있다.
 * · **오늘만 가리기** → 그날 자정까지만. 다음 아침 무조건 부활한다.
 *   **영구 숨김은 없다** — 세팅은 넘길 수 있는 일이 아니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 저장은 서버 우선, 없으면 이 브라우저
 * ─────────────────────────────────────────────────────────────
 * 정책은 서버 저장(`briefingAction`)을 요구한다 — 기기를 바꿔도 유지되어야
 * 하기 때문이다. 그런데 그 API 는 아직 배포 전이다.
 *
 * 그래서 **서버 응답의 `actions[]` 가 있으면 그것을 쓰고, 없으면
 * localStorage 로 같은 일을 한다.** 둘을 합치지 않고 서버가 이기게 두는
 * 이유는, 합치면 「서버에서 지웠는데 이 브라우저에서 되살아나는」 상태가
 * 생기기 때문이다.
 *
 * ⚠️ 폴백일 때는 화면이 **그 한계를 밝힌다** — 다른 기기에서 다시 뜨는 것을
 *    보고 「저장이 안 된다」로 읽으면 안 된다 (세팅 카드와 같은 규칙).
 */

const KEY = 'opsCoachOnboardingActions';

export const LOCAL_ONLY_NOTE = '이 브라우저에서만 기억됩니다';
/**
 * 서버 이력이 있는 센터에서 저장이 실패했을 때.
 * 로컬에 남겨도 `resolveCoachActions` 가 서버 이력을 우선해 읽지 않으므로
 * 「이 브라우저에서만 기억됩니다」라고 하면 거짓이다 — 실패를 그대로 말한다.
 * 부르는 쪽은 이때 눌린 티(임시 값)를 거둔다. 그래서 「다시 눌러 주세요」다.
 */
export const SAVE_FAILED_NOTE = '저장하지 못했습니다 · 다시 눌러 주세요';

type ActionType = 'CHECK' | 'HIDE_TODAY';
export type CoachActionType = ActionType;

/** `{ [coachId]: { CHECK?: 'YYYY-MM-DD', HIDE_TODAY?: 'YYYY-MM-DD' } }` */
type LocalStore = Record<string, Partial<Record<ActionType, string>>>;

const readLocal = (): LocalStore => {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as LocalStore) : {};
    } catch {
        /* 깨진 값이 남아 있어도 화면은 떠야 한다 — 빈 것으로 본다 */
        return {};
    }
};

const writeLocal = (store: LocalStore) => {
    try {
        window.localStorage.setItem(KEY, JSON.stringify(store));
    } catch {
        /* 사파리 프라이빗 등 — 저장만 실패하고 이번 화면은 그대로 간다 */
    }
};

/** 강사 한 명의 행동 상태 */
export type CoachActionState = {
    /** 오늘 가려 둔 카드인가 — 다음 아침 부활 */
    hiddenToday: boolean;
    /** 조용한 모드인가 (체크 후 3일) */
    quiet: boolean;
    /** 체크한 지 며칠 됐나 — 「연락함 · n일 전」 */
    quietDays: number | null;
    /** 조용한 모드가 끝났는데 아직 안 풀렸나 — 문구가 바뀐다 */
    quietExpired: boolean;
};

const EMPTY: CoachActionState = {
    hiddenToday: false,
    quiet: false,
    quietDays: null,
    quietExpired: false,
};

const stateFrom = (
    at: Partial<Record<ActionType, string>> | undefined,
    today: dayjs.Dayjs
): CoachActionState => {
    if (!at) return EMPTY;
    const day = today.startOf('day');

    /* 가리기는 **그날에만** 유효하다 — 날짜가 다르면 이미 부활한 것이다 */
    const hiddenToday = at.HIDE_TODAY != null && dayjs(at.HIDE_TODAY).isSame(day, 'day');

    const checked = at.CHECK ? dayjs(at.CHECK).startOf('day') : null;
    /*
     * 지난 날짜 화면에서 보면 체크 시각이 그 날짜보다 **뒤**일 수 있다(오늘 체크한 것을
     * 어제 화면에서 봄). 음수를 그대로 두면 「연락함 · -1일 전」이 찍히고, 사흘 판정도
     * 통과해 버린다. 「그 화면 기준으로 아직 하루도 안 지났다」 = 0 으로 본다.
     */
    const since = checked && checked.isValid() ? Math.max(0, day.diff(checked, 'day')) : null;

    return {
        hiddenToday,
        quiet: since != null && since < QUIET_DAYS,
        quietDays: since,
        /*
         * 3일이 지났는데 카드가 아직 살아 있다 = 연락했는데도 안 풀렸다.
         * 이건 **새 사실**이라 다시 말할 값어치가 있다 (정책 §4-3).
         */
        quietExpired: since != null && since >= QUIET_DAYS,
    };
};

/**
 * 화면이 쓸 행동 상태 — **서버가 주면 서버, 아니면 이 브라우저**.
 *
 * `serverActions` 가 `undefined` 면 「서버에 이 기능이 없다」는 뜻이고
 * 빈 배열이면 「서버는 아는데 이력이 없다」는 뜻이다. 둘은 다르다 —
 * 빈 배열일 때 localStorage 를 섞으면 서버에서 지운 것이 되살아난다.
 */
export const resolveCoachActions = (
    serverActions: CoachOnboardingBriefing['actions'] | undefined,
    today: dayjs.Dayjs = dayjs()
): { states: Record<string, CoachActionState>; serverBacked: boolean } => {
    if (serverActions) {
        const byCoach: LocalStore = {};
        serverActions.forEach((a) => {
            byCoach[a.coachId] = { ...byCoach[a.coachId], [a.type]: a.at };
        });
        const states: Record<string, CoachActionState> = {};
        Object.entries(byCoach).forEach(([id, at]) => {
            states[id] = stateFrom(at, today);
        });
        return { states, serverBacked: true };
    }

    const local = readLocal();
    const states: Record<string, CoachActionState> = {};
    Object.entries(local).forEach(([id, at]) => {
        states[id] = stateFrom(at, today);
    });
    return { states, serverBacked: false };
};

/**
 * 방금 누른 것을 **서버 왕복 전에** 화면에 반영한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 필요한가
 * ─────────────────────────────────────────────────────────────
 * 서버가 `actions[]` 를 주면 서버가 이긴다(위 규칙). 그런데 저장 API 가
 * 아직 배포 전이라, 그 상태에서 원장님이 「오늘만 가리기」를 누르면
 * **아무 일도 안 일어난다.** 눌렀는데 화면이 그대로면 고장으로 읽는다.
 *
 * 그래서 이번 세션에 누른 것은 서버 응답 위에 덮어 쓴다. 다음 조회에서
 * 서버 값이 오면 자연히 그것으로 대체된다 — 낙관적 반영이지 저장이 아니다.
 */
export const withOptimistic = (
    states: Record<string, CoachActionState>,
    pending: Array<{ coachId: string; type: ActionType }>,
    today: dayjs.Dayjs = dayjs()
): Record<string, CoachActionState> => {
    if (pending.length === 0) return states;
    const out = { ...states };
    pending.forEach(({ coachId, type }) => {
        const prev = out[coachId] ?? EMPTY;
        out[coachId] =
            type === 'HIDE_TODAY'
                ? { ...prev, hiddenToday: true }
                : /* 방금 체크했으므로 「오늘」이다 — 0일 전 */
                  { ...prev, quiet: true, quietDays: 0, quietExpired: false };
    });
    return out;
};

export const actionStateOf = (
    states: Record<string, CoachActionState>,
    coachId: string
): CoachActionState => states[coachId] ?? EMPTY;

/**
 * 이 브라우저에 남기는 폴백 기록.
 *
 * ⚠️ 서버 저장이 성공했으면 **부르지 않는다.** 두 곳에 남기면 서버에서
 *    지운 뒤에도 이 브라우저에서만 살아 있는 유령이 생긴다.
 */
export const recordLocalAction = (
    coachId: string,
    type: ActionType,
    today: dayjs.Dayjs = dayjs()
): void => {
    const store = readLocal();
    store[coachId] = { ...store[coachId], [type]: today.format('YYYY-MM-DD') };
    writeLocal(store);
};

/** 체크 해제 — 잘못 눌렀을 때 되돌린다 */
export const clearLocalAction = (coachId: string, type: ActionType): void => {
    const store = readLocal();
    if (!store[coachId]) return;
    delete store[coachId][type];
    writeLocal(store);
};

/**
 * 조용한 모드의 한 줄 — 「연락함 · 2일 전」.
 *
 * 🔴 조용한 모드에서는 **헤드라인을 이걸로 덮는다.** 독촉 문장을 그대로 두고
 *    작은 글씨만 더하면 조용해진 것이 아니다.
 */
export const quietLineOf = (s: CoachActionState): string | null => {
    if (!s.quiet) return null;
    if (s.quietDays === 0) return '오늘 연락하셨습니다 — 사흘간 조용히 두겠습니다';
    return `연락함 · ${s.quietDays}일 전`;
};

/**
 * 처리 표시 한 건을 **서버에 먼저** 저장하고, 안 되면 이 브라우저에 남긴다.
 *
 * 신규 센터 화면(`OnboardingBriefingScreen`)과 과도기 강사 카드(`OpsBriefingTodayPage`)가
 * 같은 순서를 쓴다. 두 화면이 각자 구현했을 때 한쪽(과도기)은 서버를 아예 안 부르고
 * 로컬에만 적었고, 서버 이력이 있는 센터는 그 기록을 읽지 않아 체크가 토스트만 남겼다
 * (2026-09-10 범계점 「권한길 강사」 신고). 포크하지 않는다.
 *
 * 반환 `note` 는 토스트 꼬리다 — 저장됐으면 빈 문자열.
 */
export const persistCoachAction = async (args: {
    coachId: string;
    type: ActionType;
    today: dayjs.Dayjs;
    /**
     * `resolveCoachActions(...).serverBacked` — 서버 이력이 있는 센터인가.
     * 함수로 주면 **저장이 끝난 시점**에 읽는다. 저장 중에 백그라운드 조회가 서버 모양을
     * 바꾸면(구서버 `undefined` ↔ 신서버 `[]`) 클릭 시점 값으로 고른 꼬리가 화면과 어긋난다.
     */
    serverBacked: boolean | (() => boolean);
    persist?: (coachId: string, type: ActionType) => Promise<boolean>;
}): Promise<{ saved: boolean; note: string }> => {
    let saved = false;
    try {
        saved = args.persist ? await args.persist(args.coachId, args.type) : false;
    } catch {
        saved = false;
    }
    if (!saved) recordLocalAction(args.coachId, args.type, args.today);
    const serverBacked = typeof args.serverBacked === 'function' ? args.serverBacked() : args.serverBacked;
    const note = saved ? '' : ` · ${serverBacked ? SAVE_FAILED_NOTE : LOCAL_ONLY_NOTE}`;
    return { saved, note };
};

/** 두 화면이 같은 문장을 쓴다 — 처리 표시의 뜻(사흘·내일 아침)을 토스트가 말한다 */
export const coachActionToast = (name: string, type: ActionType, note: string): string =>
    type === 'CHECK'
        ? `${name} 강사 · 오늘 연락함으로 표시했습니다 · 사흘간 조용히 둡니다${note}`
        : `${name} 강사 · 오늘만 가렸습니다 · 내일 아침 다시 보입니다${note}`;
